import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowRight, Users, Award, BookOpen, Heart, Sparkles, ChevronDown } from 'lucide-react';

// ── Animation variants ───────────────────────────────────────────────────────
const containerVar = {
  hidden:  { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.14, delayChildren: 0.15 } },
};
const fadeUp = {
  hidden:  { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.25, 0.46, 0.45, 0.94] } },
};
const scaleFade = {
  hidden:  { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.75, type: 'spring', stiffness: 90, damping: 18 } },
};

// ── Data ────────────────────────────────────────────────────────────────────
const STATS = [
  { Icon: Users,    label: 'Student Centred',  color: 'text-brand-indigo',  bg: 'bg-brand-indigo/12'  },
  { Icon: Award,    label: 'Excellence Driven', color: 'text-brand-amber',  bg: 'bg-brand-amber/12'   },
  { Icon: BookOpen, label: 'Learning Focused',  color: 'text-brand-emerald', bg: 'bg-brand-emerald/12' },
  { Icon: Heart,    label: 'Community Care',    color: 'text-brand-rose',    bg: 'bg-brand-rose/12'    },
];

// ── Stat Pill ────────────────────────────────────────────────────────────────
const StatPill = ({ Icon, label, color, bg, index }) => (
  <motion.div
    variants={fadeUp}
    custom={index}
    whileHover={{ y: -3, scale: 1.02 }}
    className="flex items-center gap-2.5 px-4 py-3 rounded-2xl min-w-0
               glassmorphism-card border border-white/80 shadow-md
               hover:shadow-lg hover:border-white/95
               transition-all duration-250 cursor-default"
  >
    <div className={`p-2 rounded-xl ${bg} flex-shrink-0`}>
      <Icon className={`h-4 w-4 ${color}`} />
    </div>
    <span className="text-sm font-semibold text-foreground/90 leading-snug min-w-0">
      {label}
    </span>
  </motion.div>
);

// ── Hero ─────────────────────────────────────────────────────────────────────
const Hero = () => (
  <section
    id="hero"
    className="relative min-h-screen flex flex-col items-center justify-center text-center
               mesh-gradient dot-pattern overflow-hidden py-32"
    aria-label="Meenamma Educational Trust – hero"
  >
    {/* Ambient blobs */}
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full
                      bg-brand-indigo/8 blur-[80px] animate-float" />
      <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full
                      bg-brand-emerald/8 blur-[80px] animate-float animation-delay-400" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
                      w-[400px] h-[400px] rounded-full bg-brand-amber/6 blur-[60px]
                      animate-float animation-delay-200" />
      <div className="absolute top-1/3 right-1/4 w-[320px] h-[320px] rounded-full
                      bg-brand-rose/5 blur-[65px] animate-float animation-delay-600" />
      {/* Decorative dashed rings */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
                      w-[700px] h-[700px] deco-ring opacity-60" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
                      w-[900px] h-[900px] deco-ring opacity-30" />
    </div>

    {/* Background photo overlay */}
    <div className="absolute inset-0 opacity-8" aria-hidden="true">
      <img
        src="https://images.unsplash.com/photo-1581726690015-c9861fa5057f?q=80&w=2070&auto=format&fit=crop"
        alt=""
        className="w-full h-full object-cover blur-sm"
        loading="eager"
        decoding="async"
        onContextMenu={(e) => e.preventDefault()}
      />
      {/* Vignette overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-background/40 via-transparent to-background/20" />
    </div>

    {/* Content */}
    <motion.div
      className="relative z-10 container mx-auto px-4 sm:px-6 flex flex-col items-center"
      variants={containerVar}
      initial="hidden"
      animate="visible"
    >
      {/* Badge */}
      <motion.div variants={fadeUp} className="mb-7">
        <span className="hero-badge">
          <Sparkles className="h-3.5 w-3.5 animate-pulse-subtle" />
          Empowering Students Since 2024
        </span>
      </motion.div>

      {/* Title */}
      <motion.div variants={scaleFade} className="mb-7">
        <h1 className="leading-none">
          <span className="font-logo gradient-text block text-6xl sm:text-7xl md:text-8xl lg:text-9xl leading-[0.9]">
            Meenamma
          </span>
          {/* Decorative divider between title parts */}
          <motion.div
            className="flex justify-center items-center gap-3 my-3"
            initial={{ scaleX: 0, opacity: 0 }}
            animate={{ scaleX: 1, opacity: 1 }}
            transition={{ delay: 0.55, duration: 0.5, ease: 'easeOut' }}
          >
            <div className="wave-divider" />
          </motion.div>
          <span className="font-logo text-foreground/90 block text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-none tracking-tight">
            Educational Trust
          </span>
        </h1>
      </motion.div>

      {/* Tagline */}
      <motion.p
        variants={fadeUp}
        className="text-base md:text-lg lg:text-xl text-muted-foreground/85 max-w-2xl mx-auto
                   mb-10 leading-relaxed font-medium"
      >
        Empowering bright minds through accessible education — offering scholarships,
        academic support, and mentorship to talented students across Srikakulam district.
      </motion.p>

      {/* CTAs */}
      <motion.div
        variants={fadeUp}
        className="flex flex-col sm:flex-row gap-3.5 justify-center items-center mb-14"
      >
        <Button
          size="lg"
          className="px-9 h-13 text-[15px] shadow-xl shadow-primary/30
                     hover:shadow-2xl hover:shadow-primary/35 transition-all"
          asChild
        >
          <a href="#about">
            Learn About Us
            <ArrowRight className="ml-2 h-5 w-5" />
          </a>
        </Button>
        <Button
          variant="outline"
          size="lg"
          className="px-9 h-13 text-[15px] border-2 border-primary/30
                     hover:bg-primary/7 hover:border-primary/50
                     backdrop-blur-sm bg-white/20 dark:bg-white/5"
          asChild
        >
          <a href="#impact">Join Our Cause</a>
        </Button>
      </motion.div>

      {/* Stat pills */}
      <motion.div
        variants={containerVar}
        className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-3xl mx-auto w-full"
      >
        {STATS.map((s, i) => <StatPill key={s.label} {...s} index={i} />)}
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        variants={fadeUp}
        className="mt-14 flex flex-col items-center gap-2.5 text-muted-foreground/50"
      >
        <span className="text-xs font-medium tracking-widest uppercase">Scroll to Explore</span>
        <div className="relative flex items-center justify-center mt-0.5">
          {/* Pulsing ring */}
          <span className="absolute w-10 h-10 rounded-full border border-primary/25 ring-pulse" aria-hidden="true" />
          <motion.div
            animate={{ y: [0, 6, 0] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: 'easeInOut' }}
          >
            <ChevronDown className="h-5 w-5" />
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  </section>
);

export default Hero;
